import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { productUrl } from '../lib/productUrl';
import { useTranslation } from 'react-i18next';
import { productsApi } from '../lib/api';
import { useTrackCategoryView } from '../lib/tracking';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Plus, ArrowUpDown, Filter, X, ChevronRight, Camera, Lightbulb, Mic, Film, Box, Truck, Tag, Zap, Tv, Wrench, Sparkles } from 'lucide-react';

const ITEMS_PER_PAGE = 48;

// Map nav-menu names to Lucide icons for the subcategory landing tiles.
// Falls back to Box if no specific icon is defined.
const NAV_ICONS = {
  IMAGE: Camera,
  AUDIO: Mic,
  LUMIÈRE: Lightbulb,
  LUMIERE: Lightbulb,
  MACHINERIE: Film,
  'LE GRIP': Wrench,
  'LA RÉGIE': Tv,
  'LA REGIE': Tv,
  'LIVE ET STREAM': Tv,
  CONSOMMABLES: Sparkles,
  ÉNERGIE: Zap,
  ENERGIE: Zap,
  'LIVRAISON ET VÉHICULE': Truck,
  'LIVRAISON ET VEHICULE': Truck,
  OCCASIONS: Tag,
};

const iconForCategory = (name, fallback) => NAV_ICONS[(name || '').toUpperCase()] || NAV_ICONS[(fallback || '').toUpperCase()] || Box;

const CataloguePage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('default');
  const [showAll, setShowAll] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [filters, setFilters] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [menuHierarchy, setMenuHierarchy] = useState(null);
  const { cart, addItem } = useCart();
  const { user } = useAuth();
  const cartItems = cart?.items || [];
  const loaderRef = useRef(null);

  const categoryFilter = searchParams.get('category') || '';
  const searchFilter = searchParams.get('search') || '';
  const navMenu = searchParams.get('nav_menu') || '';
  const navSousMenu = searchParams.get('nav_sous_menu') || '';
  const navSousSousMenu = searchParams.get('nav_sous_sous_menu') || '';
  // When the user clicks "Voir tous les produits de XXX" from the landing,
  // we add ?show_all_branch=1 to bypass the subcategory tile grid.
  const showAllBranch = searchParams.get('show_all_branch') === '1';

  // Show the subcategory landing page when the user clicks a parent
  // menu (nav_menu set, no sub-level). Bypasses product fetch entirely.
  const currentMenu = menuHierarchy && navMenu && !navSousMenu && !navSousSousMenu && !searchFilter && !showAllBranch
    ? menuHierarchy.find((m) => m.name === navMenu)
    : null;
  const showSubcategoryLanding = !!(currentMenu && (currentMenu.children || []).length > 0);

  useTrackCategoryView(categoryFilter);

  // Fetch the navigation hierarchy once — used to render the subcategory
  // landing page when the user clicks a parent menu.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/hierarchy`)
      .then((r) => r.json())
      .then((data) => setMenuHierarchy(data.menus || []))
      .catch(() => setMenuHierarchy([]));
  }, []);

  // Fetch products (paginated or all)
  useEffect(() => {
    // Subcategory landing → don't fetch products at all.
    if (showSubcategoryLanding) {
      setLoading(false);
      setProducts([]);
      setAllProducts([]);
      setTotalProducts(0);
      return;
    }
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const sortParam = sortBy !== 'default' ? sortBy : undefined;
        
        if (showAll) {
          // Fetch ALL products for infinite scroll
          if (navMenu) {
            const params = new URLSearchParams({ nav_menu: navMenu, page: '1', limit: '9999', lang: i18n.language });
            if (navSousMenu) params.set('nav_sous_menu', navSousMenu);
            if (navSousSousMenu) params.set('nav_sous_sous_menu', navSousSousMenu);
            if (sortParam) params.set('sort', sortParam);
            if (selectedBrand) params.set('brand', selectedBrand);
            if (priceMin) params.set('price_min', priceMin);
            if (priceMax) params.set('price_max', priceMax);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/products?${params}`);
            const data = await response.json();
            setAllProducts(data.products || []);
            setTotalProducts(data.total || 0);
          } else {
            const params = { page: 1, limit: 9999, lang: i18n.language };
            if (categoryFilter) params.category = categoryFilter;
            if (searchFilter) params.search = searchFilter;
            if (sortParam) params.sort = sortParam;
            if (selectedBrand) params.brand = selectedBrand;
            if (priceMin) params.price_min = priceMin;
            if (priceMax) params.price_max = priceMax;
            const response = await productsApi.getProducts(params);
            setAllProducts(response.data.products || []);
            setTotalProducts(response.data.total || 0);
          }
          setVisibleCount(ITEMS_PER_PAGE);
          setProducts([]);
        } else {
          if (navMenu) {
            const params = new URLSearchParams({ nav_menu: navMenu, page: page.toString(), limit: ITEMS_PER_PAGE.toString(), lang: i18n.language });
            if (navSousMenu) params.set('nav_sous_menu', navSousMenu);
            if (navSousSousMenu) params.set('nav_sous_sous_menu', navSousSousMenu);
            if (sortParam) params.set('sort', sortParam);
            if (selectedBrand) params.set('brand', selectedBrand);
            if (priceMin) params.set('price_min', priceMin);
            if (priceMax) params.set('price_max', priceMax);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/products?${params}`);
            const data = await response.json();
            setProducts(data.products || []);
            setTotalProducts(data.total || 0);
          } else {
            const params = { page, limit: ITEMS_PER_PAGE, lang: i18n.language };
            if (categoryFilter) params.category = categoryFilter;
            if (searchFilter) params.search = searchFilter;
            if (sortParam) params.sort = sortParam;
            if (selectedBrand) params.brand = selectedBrand;
            if (priceMin) params.price_min = priceMin;
            if (priceMax) params.price_max = priceMax;
            const response = await productsApi.getProducts(params);
            setProducts(response.data.products || []);
            setTotalProducts(response.data.total || 0);
          }
          setAllProducts([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setProducts([]);
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categoryFilter, searchFilter, navMenu, navSousMenu, navSousSousMenu, page, i18n.language, sortBy, showAll, selectedBrand, priceMin, priceMax, showSubcategoryLanding]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setShowAll(false);
    setVisibleCount(ITEMS_PER_PAGE);
  }, [categoryFilter, searchFilter, navMenu, navSousMenu, navSousSousMenu, selectedBrand, priceMin, priceMax]);

  // Infinite scroll observer
  useEffect(() => {
    if (!showAll || !loaderRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleCount < allProducts.length) {
          setVisibleCount(prev => Math.min(prev + 24, allProducts.length));
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [showAll, visibleCount, allProducts.length]);

  // Fetch recommendations
  useEffect(() => {
    const fetchRecs = async () => {
      setRecsLoading(true);
      try {
        const params = { lang: i18n.language };
        if (categoryFilter) params.category = categoryFilter;
        if (user?.email) params.user_email = user.email;
        const cartIds = cartItems.map(i => i.product_id).filter(Boolean).join(',');
        if (cartIds) params.cart_ids = cartIds;
        const response = await productsApi.getCatalogRecommendations(params);
        setRecommendations(response.data.recommendations || []);
      } catch (error) {
        setRecommendations([]);
      } finally {
        setRecsLoading(false);
      }
    };
    fetchRecs();
  }, [categoryFilter, i18n.language, user?.email, cartItems.length]);

  // Fetch available filters (brands, price range)
  useEffect(() => {
    productsApi.getFilters().then(res => setFilters(res.data)).catch(() => {});
  }, []);

  // Sale items (SKU prefix "vente") are pushed to the end of the catalogue
  // while preserving the back-office order for everything else (which is the
  // menu order). Stable sort: items keep their relative position within each
  // group (non-sale items, then sale items).
  const sortSalesLast = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return arr;
    const nonSale = [];
    const sale = [];
    for (const p of arr) {
      const sku = String(p?.product_sku || p?.sku || p?.ref || '').toLowerCase();
      if (sku.startsWith('vente')) sale.push(p);
      else nonSale.push(p);
    }
    return sale.length ? [...nonSale, ...sale] : arr;
  };

  const displayProducts = sortSalesLast(showAll ? allProducts.slice(0, visibleCount) : products);
  const totalPages = showAll ? 1 : Math.ceil(totalProducts / ITEMS_PER_PAGE);

  const handleSortChange = (value) => {
    if (value === 'show_all') {
      setShowAll(true);
      setSortBy('default');
    } else {
      setShowAll(false);
      setSortBy(value);
    }
  };

  return (
    <div className="min-h-screen py-6" data-testid="catalogue-page">
      <SEO
        title={navMenu ? `${navMenu}${navSousMenu ? ` — ${navSousMenu}` : ''} — Catalogue` : 'Catalogue'}
        description={`Catalogue de location de matériel audiovisuel professionnel${navMenu ? ` — ${navMenu}` : ''}. ${totalProducts} produits disponibles. Puzzle Video Paris.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": navMenu || "Catalogue Puzzle Video",
          "description": `Catalogue location matériel audiovisuel${navMenu ? ` — ${navMenu}` : ''}`,
          "numberOfItems": totalProducts
        }}
      />
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">{t('catalogue.title')}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="catalogue-heading">
            {searchFilter
              ? `${t('catalogue.results_for')} "${searchFilter}"`
              : navMenu
                ? navSousMenu
                  ? `${navMenu} — ${navSousMenu}${navSousSousMenu ? ` / ${navSousSousMenu}` : ''}`
                  : navMenu
                : t('catalogue.all_products')}
          </h1>
        </div>

        {/* Subcategory landing — shown when a parent menu is clicked.
            Renders a tile grid of children instead of the product list. */}
        {showSubcategoryLanding ? (
          <div data-testid="subcategory-landing">
            <p className="text-muted-foreground text-sm mb-6">
              {t('catalogue.pick_subcategory', 'Sélectionnez une sous-catégorie pour découvrir les produits.')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {(currentMenu.children || []).map((child) => {
                const Icon = iconForCategory(child.name, currentMenu.name);
                const linkTo = `/catalogue?nav_menu=${encodeURIComponent(navMenu)}&nav_sous_menu=${encodeURIComponent(child.name)}`;
                const gcCount = (child.grandchildren || child.children || []).length;
                return (
                  <Link
                    key={child.name}
                    to={linkTo}
                    className="group relative border border-border bg-white hover:border-primary hover:shadow-sm transition-all p-5 flex flex-col gap-3"
                    data-testid={`subcategory-tile-${child.name}`}
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Icon strokeWidth={1.5} className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-base leading-tight">{child.name}</h3>
                    {gcCount > 0 && (
                      <p className="text-xs text-muted-foreground">{gcCount} {t('home.subcategories', 'sous-catégories')}</p>
                    )}
                    <ChevronRight className="absolute top-4 right-4 w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
            {/* Fallback CTA — "see everything" if user wants to browse all */}
            <div className="mt-8 text-center">
              <Link
                to={`/catalogue?nav_menu=${encodeURIComponent(navMenu)}&nav_sous_menu=__ALL__`}
                onClick={(e) => {
                  e.preventDefault();
                  // Trick: navigate with a marker that browses the whole branch.
                  // We just keep nav_menu and add a hash so the user can see all.
                  navigate(`/catalogue?nav_menu=${encodeURIComponent(navMenu)}&show_all_branch=1`);
                }}
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                data-testid="see-all-in-menu"
              >
                {t('catalogue.see_all_in', 'Voir tous les produits de')} {navMenu}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : (
        <>

        {/* Advanced Filters Bar */}
        <div className="mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            {t('catalogue.filters', 'Filtres avancés')}
            {(selectedBrand || priceMin || priceMax) && (
              <span className="w-2 h-2 rounded-full bg-primary" />
            )}
          </button>

          {showFilters && filters && (
            <div className="flex flex-wrap items-end gap-3 p-3 border border-border bg-surface mb-2" data-testid="filters-panel">
              {/* Brand filter */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('catalogue.brand')}
                </label>
                <Select value={selectedBrand || 'all'} onValueChange={(v) => setSelectedBrand(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-none h-8 text-xs w-[160px]" data-testid="brand-filter">
                    <SelectValue placeholder={t('catalogue.all_brands')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('catalogue.all_brands')}</SelectItem>
                    {filters.brands.map(b => (
                      <SelectItem key={b.name} value={b.name}>
                        {b.name} ({b.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price range */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('catalogue.price_range')}
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    className="w-20 h-8 px-2 border border-border text-xs bg-background"
                    data-testid="price-min"
                    min="0"
                    step="1"
                  />
                  <span className="text-xs text-muted-foreground">—</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className="w-20 h-8 px-2 border border-border text-xs bg-background"
                    data-testid="price-max"
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              {/* Clear filters */}
              {(selectedBrand || priceMin || priceMax) && (
                <button
                  onClick={() => { setSelectedBrand(''); setPriceMin(''); setPriceMax(''); }}
                  className="flex items-center gap-1 h-8 px-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-3 h-3" />
                  {t('catalogue.clear_filters', 'Effacer')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-6">
          {/* Right-side: product recommendations (replaces menu hierarchy on category pages) */}
          <aside className="hidden lg:block w-64 flex-shrink-0 order-2" data-testid="recommendations-sidebar">
            <div className="sticky top-24">
              <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-3">
                {t('catalogue.recommendations')}
              </h2>
              <div className="space-y-2">
                {recsLoading ? (
                  [...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex gap-2">
                      <div className="w-12 h-12 bg-gray-200 flex-shrink-0" />
                      <div className="flex-1"><div className="h-3 bg-gray-200 mb-1" /><div className="h-3 bg-gray-200 w-1/2" /></div>
                    </div>
                  ))
                ) : recommendations.length > 0 ? (
                  recommendations.map((rec) => (
                    <Link
                      key={rec.id}
                      to={productUrl(rec)}
                      data-testid={`rec-card-${rec.id}`}
                    >
                      <div className="group flex gap-3 p-2 border border-border hover:shadow-sm hover:border-primary/30 transition-all duration-200">
                        <div className="w-[72px] h-[72px] bg-surface flex-shrink-0 overflow-hidden">
                          <img
                            src={rec.image_url || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100'}
                            alt={rec.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <h3 className="font-medium text-xs leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                            {rec.name}
                          </h3>
                          <div className="flex items-center justify-between mt-1">
                            {rec.daily_price > 0 ? (
                              <span className="text-xs font-bold text-primary">{rec.daily_price.toFixed(2)} €/j</span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">{t('product.quote_only')}</span>
                            )}
                            <button
                              className="w-6 h-6 flex items-center justify-center border border-black hover:bg-primary hover:text-white hover:border-primary transition-colors"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); addItem(rec); }}
                              data-testid={`rec-add-${rec.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : null}
              </div>
            </div>
          </aside>

          {/* Product grid */}
          <main className="flex-1 order-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-gray-200 mb-3" />
                    <div className="h-3 bg-gray-200 mb-1.5 w-1/4" />
                    <div className="h-4 bg-gray-200 mb-1.5" />
                    <div className="h-5 bg-gray-200 w-1/3" />
                  </div>
                ))}
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-xl font-semibold mb-2">{t('catalogue.no_results')}</p>
                <p className="text-muted-foreground mb-6">{t('catalogue.no_results_desc')}</p>
                <Link to="/catalogue">
                  <Button variant="outline" className="rounded-none">{t('catalogue.all_products')}</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Top bar: count + sort */}
                <div className="flex items-center justify-between mb-4 gap-4">
                  <p className="text-sm text-muted-foreground">
                    {t('catalogue.products_count', { count: totalProducts })}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleSortChange('show_all')}
                      className={`text-xs px-3 py-1.5 border transition-colors ${showAll ? 'bg-black text-white border-black' : 'border-border text-muted-foreground hover:border-black hover:text-foreground'}`}
                      data-testid="show-all-btn"
                    >
                      {t('catalogue.show_all')}
                    </button>
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
                      <Select value={sortBy} onValueChange={handleSortChange}>
                        <SelectTrigger className="rounded-none h-8 text-xs w-[180px]" data-testid="sort-select">
                          <SelectValue placeholder={t('catalogue.sort_default')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">{t('catalogue.sort_default')}</SelectItem>
                          <SelectItem value="price_desc">{t('catalogue.sort_price_desc')}</SelectItem>
                          <SelectItem value="price_asc">{t('catalogue.sort_price_asc')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Mobile recommendations hidden on catalogue pages */}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
                  {displayProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Infinite scroll loader */}
                {showAll && visibleCount < allProducts.length && (
                  <div ref={loaderRef} className="py-8 text-center">
                    <div className="animate-pulse text-sm text-muted-foreground">Chargement...</div>
                  </div>
                )}

                {/* Pagination — only when not showing all */}
                {!showAll && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10" data-testid="pagination">
                    <Button variant="outline" className="rounded-none h-9 text-xs" disabled={page === 1} onClick={() => { setPage(p => p - 1); window.scrollTo(0, 0); }} data-testid="pagination-prev">
                      {t('catalogue.previous')}
                    </Button>
                    <span className="text-sm font-medium px-3" data-testid="pagination-info">
                      Page {page}/{totalPages}
                    </span>
                    <Button variant="outline" className="rounded-none h-9 text-xs" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); window.scrollTo(0, 0); }} data-testid="pagination-next">
                      {t('catalogue.next')}
                    </Button>
                  </div>
                )}

                {showAll && allProducts.length > 0 && (
                  <p className="text-center text-xs text-muted-foreground mt-6">
                    {visibleCount >= allProducts.length
                      ? `${allProducts.length} produits affichés`
                      : `${visibleCount} / ${allProducts.length} produits`}
                  </p>
                )}
              </>
            )}
          </main>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default CataloguePage;
